import React, { useEffect, useState } from 'react';
import Sidebar from '../../components/Sidebar/Sidebar';
import { RewardsProps } from './types';
import { connect, useDispatch } from 'react-redux';
import { AppState } from '../../store/rootReducer';
import { getUserId } from '../../store/auth/selectors';
import SidebarSecond from '../../components/Sidebar/SidebarSecond';
import { Alert, Box, Button, OutlinedInput } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { autoId } from '../../utils/UidGenerator';
import AddImage from '../../components/AddImage/AddImage';
import strings from '../../resources/strings';
import { LanguagesBar } from '../../components/LanguagesBar';
import { getLanguagesList } from '../../store/languages/selectors';
import { requestGetList } from '../../store/languages/actions';
import { RewardsCategory } from './RewardsPageInterfaces';
import consts from './consts';
import SnackbarMessage from '../../components/SnackbarMessage/SnackbarMessage';
import { validateLanguages } from '../../utils/Validator';
import { storage } from '../../resources/storage';
import './RewardsPage.scss';
import { Reward } from '../../store/rewards/types';
import { requestGetListRewards, requestSaveReward } from '../../store/rewards/actions';
import { getRewardsList } from '../../store/rewards/selectors';

const RewardsPage: React.FC<RewardsProps> = (props) => {
    const [error, setError] = useState<boolean>(false);
    const [rewardsTypes, setRewardsTypes] = useState<RewardsCategory[]>([]);
    const [rewardsFilteredItems, setRewardsFilteredItems] = useState<Reward[]>([]);
    const [currentType, setCurrentType] = useState<RewardsCategory>();
    const [currentReward, setCurrentReward] = useState<Reward>();
    const [languageValue, setLanguageValue] = React.useState('pl');
    const [successMessage, setSuccessMessage] = React.useState('');
    const [image, setImage] = useState('');
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(requestGetListRewards());
        setRewardsTypes(consts.rewordTypeList);
        dispatch(requestGetList());
    }, []);

    useEffect(() => {
        if (!currentType) setCurrentType(rewardsTypes.find((x) => getTypeName(x) === props.rewards[0].type));
    }, [props.rewards]);

    useEffect(() => {
        setCurrentReward({
            ...currentReward,
            image: image,
        } as Reward);
    }, [image]);

    useEffect(() => {
        const currentListName = getTypeName(currentType);
        setRewardsFilteredItems(props.rewards.filter((item) => item.type.toLowerCase() === currentListName));
    }, [currentType, props.rewards]);

    const createEmptyRecord = () => {
        let record = {};
        props.languages.forEach((item) => {
            record = { ...record, [item.code]: '' };
        });
        return record;
    };

    const getTypeName = (type: RewardsCategory | undefined) => {
        return type?.name?.['en'].toLowerCase() ?? '';
    };

    const createNewReward = () => {
        const newEventToAdd = {
            id: autoId(),
            image: '',
            type: getTypeName(currentType),
            name: createEmptyRecord(),
        } as Reward;
        rewardsFilteredItems.push(newEventToAdd);
        setCurrentReward(newEventToAdd);
    };
    const capitalizeFirstLetter = (string: string) =>
        string ? string.charAt(0).toUpperCase() + string.slice(1) : undefined;

    return (
        <div className="content">
            <Sidebar></Sidebar>
            <SidebarSecond
                data={rewardsTypes}
                actualItemId={currentType?.id}
                title={strings.rewards.title}
                isAddNew={false}
                hideStatus={true}
                setActualItemId={(id: string) => {
                    setCurrentType(rewardsTypes.find((el) => el.id === id));
                    setCurrentReward(undefined);
                }}
                actualLanguage={'en'}
            ></SidebarSecond>
            <SidebarSecond
                data={rewardsFilteredItems}
                actualItemId={currentReward?.id}
                createNewItem={() => createNewReward()}
                title={currentType ? capitalizeFirstLetter(getTypeName(currentType)) : ''}
                isAddNew={true}
                hideStatus={true}
                setActualItemId={(id: string) => {
                    setCurrentReward(rewardsFilteredItems.find((el) => el.id === id));
                    setSuccessMessage('');
                }}
                actualLanguage={languageValue}
            ></SidebarSecond>
            {!currentReward ? null : (
                <div className="main">
                    <LanguagesBar
                        languages={props.languages}
                        selectedLanguage={languageValue}
                        handleChange={setLanguageValue}
                    ></LanguagesBar>
                    <Box sx={{ marginTop: '2rem' }}>
                        <OutlinedInput
                            placeholder={
                                currentType
                                    ? `${capitalizeFirstLetter(getTypeName(currentType))} ${
                                          strings.rewards.namePlaceholder
                                      }`
                                    : strings.rewards.namePlaceholder
                            }
                            value={currentReward.name?.[languageValue]}
                            onChange={(event) => {
                                setCurrentReward({
                                    ...currentReward,
                                    name: { ...currentReward.name, [languageValue]: event.target.value },
                                });
                            }}
                            sx={{
                                margin: '2rem 0',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '1rem',
                                maxWidth: '600px',
                                width: '100%',
                            }}
                        />
                    </Box>
                    <Box className="rewards-img">
                        <h3 className="main-name">{strings.newsAndEvents.image}</h3>
                        <AddImage
                            path={storage.rewards}
                            showMessage={false}
                            sendOnDrop={true}
                            actualEventFile={currentReward.image}
                            actualEventId={currentReward.id}
                            setGetImgAddress={(url: string) => setImage(url)}
                        ></AddImage>
                    </Box>

                    {successMessage.length > 0 ? (
                        <Box>
                            <Alert
                                sx={{
                                    fontSize: '1.6rem',
                                    margin: '1rem auto 0 auto',
                                    maxWidth: '600px',
                                }}
                                variant="outlined"
                                severity="success"
                            >
                                {successMessage}
                            </Alert>
                        </Box>
                    ) : null}
                    <Box>
                        <SnackbarMessage
                            serverity="error"
                            message={error ? strings.languagesValidation.errorMessage : ''}
                            clearMessage={() => {
                                setError(false);
                            }}
                        ></SnackbarMessage>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '1rem',
                            paddingRight: '2rem',
                            marginTop: '2rem',
                        }}
                    >
                        <Button
                            onClick={() => {
                                if (!validateLanguages([currentReward], props.languages.length)) {
                                    if (setError) setError(true);
                                    return;
                                }
                                dispatch(requestSaveReward(currentReward));
                                setSuccessMessage(strings.newsAndEvents.successSave);
                            }}
                            variant="outlined"
                            startIcon={<SaveIcon />}
                            sx={{ backgroundColor: '#E3F2FD', color: '#2196F3' }}
                        >
                            {strings.newsAndEvents.buttons.save}
                        </Button>
                    </Box>
                </div>
            )}
        </div>
    );
};

function mapStateToProps(state: AppState) {
    return {
        userId: getUserId(state),
        languages: getLanguagesList(state),
        rewards: getRewardsList(state),
    };
}

export default connect(mapStateToProps)(RewardsPage);
